import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { JSX } from 'react'
import { describeError } from '../api/errors'
import type { Assignment, GameFull, RoomView, SessionView } from '../api/rooms'
import { games as gamesApi, rooms as roomsApi, sessions as sessionsApi } from '../api/rooms'
import { ProjectSocket, roomWsUrl } from '../api/ws'
import type { RecordedTake } from '../audio/recorder'
import { PlayRun } from '../components/play/PlayRun'
import { RoomLobby } from '../components/room/RoomLobby'
import { RoomWaiting } from '../components/room/RoomWaiting'
import { buildRoomPlaylist } from '../game/roomOrder'
import { readAutoPlayTake, readListenOriginal } from '../game/settings'
import { navigate } from '../lib/router'
import { pushToast } from '../lib/toast'

/**
 * Комната: лобби, сетевая игра и результат.
 *
 * Состояние приходит целиком по WebSocket (событие room.state), поэтому после
 * разрыва связи экран перерисовывается от свежего снимка, а не доклеивает
 * пропущенные события (docs/ROOMS.md).
 */
export function RoomPage({ code }: { code: string }): JSX.Element {
  const [view, setView] = useState<RoomView | null>(null)
  const [session, setSession] = useState<SessionView | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [name, setName] = useState('')
  const [busy, setBusy] = useState(false)
  const mounted = useRef(true)

  useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
    }
  }, [])

  const loadRoom = useCallback(async () => {
    try {
      const next = await roomsApi.get(code)
      if (!mounted.current) return
      setView(next)
      setError(null)
      if (next.room.sessionId) {
        try {
          setSession(await sessionsApi.get(next.room.sessionId))
        } catch {
          setSession(null)
        }
      } else {
        setSession(null)
      }
    } catch (err) {
      if (mounted.current) setError(describeError(err))
    }
  }, [code])

  useEffect(() => {
    void loadRoom()
  }, [loadRoom])

  // Живые события комнаты. Любое из них — повод перечитать состояние целиком:
  // комната маленькая, а точная синхронизация важнее экономии запросов.
  useEffect(() => {
    const socket = new ProjectSocket(roomWsUrl(code))
    const off = socket.onEvent(() => {
      void loadRoom()
    })
    socket.connect()
    return () => {
      off()
      socket.dispose()
    }
  }, [code, loadRoom])

  const join = async (): Promise<void> => {
    const clean = name.trim()
    if (!clean) return
    setBusy(true)
    try {
      const next = await roomsApi.join(code, clean)
      setView(next)
      setError(null)
    } catch (err) {
      pushToast('error', describeError(err))
    } finally {
      setBusy(false)
    }
  }

  const me = view?.me ?? null
  const isAdmin = view?.isAdmin ?? false

  const uploadTake = useCallback(
    async (lineId: string, result: RecordedTake): Promise<null> => {
      if (!session) return null
      await sessionsApi.upload(session.session.id, lineId, result.blob, {
        duration: result.duration,
        offset: result.offset,
      })
      setSession(await sessionsApi.get(session.session.id))
      return null
    },
    [session],
  )

  const myPlaylist = useMemo(() => {
    if (!session || !me) return []
    return buildRoomPlaylist(session, me.id)
  }, [session, me])

  const recordedIds = useMemo(() => new Set(session?.recorded ?? []), [session])

  if (error && !view) {
    return (
      <div className="page narrow">
        <h1>Комната не найдена</h1>
        <p className="faint">{error}</p>
        <button type="button" className="ghost" onClick={() => navigate({ name: 'games' })}>
          ← К списку озвучек
        </button>
      </div>
    )
  }

  if (!view) return <p className="faint page">Загружаю комнату…</p>

  // Гость ещё не представился — единственное, что от него нужно.
  if (!me) {
    return (
      <div className="page narrow">
        <h1>{view.room.title}</h1>
        <p className="faint">
          Комната <span className="mono">{view.room.code}</span>. Участников: {view.participants.length}
        </p>
        <form
          className="login-form"
          onSubmit={(e) => {
            e.preventDefault()
            void join()
          }}
        >
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Как вас зовут?"
            maxLength={40}
            autoFocus
            aria-label="Ваше имя"
          />
          <button type="submit" className="primary" disabled={busy || !name.trim()}>
            Войти
          </button>
        </form>
      </div>
    )
  }

  // Идёт игра — и у меня есть что записывать.
  if (view.room.state === 'playing' && session && session.session.state === 'recording') {
    const done = myPlaylist.length > 0 && myPlaylist.every((item) => recordedIds.has(item.line.id))
    if (myPlaylist.length === 0 || done) {
      return (
        <RoomWaiting
          view={view}
          session={session}
          isAdmin={isAdmin}
          onFinish={() => {
            void sessionsApi
              .finish(session.session.id)
              .catch((err: unknown) => pushToast('error', describeError(err)))
          }}
        />
      )
    }
    return (
      <PlayRun
        items={myPlaylist}
        videoSrc={gamesApi.videoUrl(session.game.game.id)}
        recordedIds={recordedIds}
        listenOriginal={readListenOriginal()}
        autoPlayTake={readAutoPlayTake()}
        onRecorded={() => undefined}
        uploadTake={uploadTake}
        onExit={() => navigate({ name: 'games' })}
        onFinish={() => undefined}
      />
    )
  }

  if (session && (session.session.state === 'rendering' || view.room.state === 'result')) {
    return (
      <RoomWaiting
        view={view}
        session={session}
        isAdmin={isAdmin}
        onFinish={() => undefined}
        onBack={() => {
          void roomsApi.back(view.room.id).catch((err: unknown) => pushToast('error', describeError(err)))
        }}
      />
    )
  }

  return (
    <RoomLobby
      view={view}
      isAdmin={isAdmin}
      onReload={() => void loadRoom()}
      onStart={async (game: GameFull, assignments: Assignment[]) => {
        try {
          const started = await roomsApi.start(view.room.id, game.game.id, assignments)
          setSession(started)
          await loadRoom()
        } catch (err) {
          pushToast('error', describeError(err))
        }
      }}
    />
  )
}
